import { supabase } from '../supabase';

export interface DbClient {
  id: string;
  consultant_id: string;
  name: string;
  created_at: string;
}

export interface DbVacancy {
  id: string;
  client_id: string;
  title: string;
  vacancy_text: string;
  notes: string;
  shared: boolean;
  share_token?: string;
  review_completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface VacancyWithClient extends DbVacancy {
  client: DbClient;
}

export async function createClient(consultantId: string, name: string): Promise<DbClient | null> {
  const { data, error } = await supabase
    .from('clients')
    .insert({
      consultant_id: consultantId,
      name
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating client:', error);
    return null;
  }

  return data;
}

export async function getClients(consultantId?: string): Promise<DbClient[]> {
  let query = supabase
    .from('clients')
    .select('*');

  if (consultantId) {
    query = query.eq('consultant_id', consultantId);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching clients:', error);
    return [];
  }

  return data || [];
}

export async function createVacancy(
  clientId: string,
  vacancy: {
    title: string;
    vacancy_text: string;
    notes?: string;
  }
): Promise<DbVacancy | null> {
  const { data, error } = await supabase
    .from('vacancies')
    .insert({
      client_id: clientId,
      title: vacancy.title,
      vacancy_text: vacancy.vacancy_text,
      notes: vacancy.notes || ''
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating vacancy:', error);
    return null;
  }

  return data;
}

export async function getVacancies(consultantId?: string): Promise<VacancyWithClient[]> {
  let query = supabase
    .from('vacancies')
    .select(`
      *,
      client:clients(*)
    `);

  if (consultantId) {
    query = query.eq('client.consultant_id', consultantId);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching vacancies:', error);
    return [];
  }

  return (data || []) as unknown as VacancyWithClient[];
}

export async function getVacanciesByClient(clientId: string): Promise<DbVacancy[]> {
  const { data, error } = await supabase
    .from('vacancies')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching vacancies by client:', error);
    return [];
  }

  return data || [];
}

export async function updateVacancy(
  vacancyId: string,
  updates: Partial<{
    title: string;
    vacancy_text: string;
    notes: string;
    shared: boolean;
  }>
): Promise<DbVacancy | null> {
  const { data, error } = await supabase
    .from('vacancies')
    .update(updates)
    .eq('id', vacancyId)
    .select()
    .single();

  if (error) {
    console.error('Error updating vacancy:', error);
    return null;
  }

  return data;
}

export async function deleteVacancy(vacancyId: string): Promise<boolean> {
  const { error } = await supabase
    .from('vacancies')
    .delete()
    .eq('id', vacancyId);

  if (error) {
    console.error('Error deleting vacancy:', error);
    return false;
  }

  return true;
}

export async function shareVacancy(vacancyId: string): Promise<boolean> {
  const { error } = await supabase
    .from('vacancies')
    .update({ shared: true })
    .eq('id', vacancyId);

  if (error) {
    console.error('Error sharing vacancy:', error);
    return false;
  }

  return true;
}

export async function getVacancyWithClient(vacancyId: string): Promise<VacancyWithClient | null> {
  const { data, error } = await supabase
    .from('vacancies')
    .select(`
      *,
      client:clients(*)
    `)
    .eq('id', vacancyId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching vacancy with client:', error);
    return null;
  }

  return data as unknown as VacancyWithClient | null;
}
